import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import { KpiStrip } from './fabDashboard/KpiStrip';
import { FloorNow } from './fabDashboard/FloorNow';
import { ProjectBoard } from './fabDashboard/ProjectBoard';
import { WeekPanels } from './fabDashboard/WeekPanels';
import { SteelPricingPanel } from './fabDashboard/SteelPricingPanel';
import { FLAG_META, fmtTime } from './fabDashboard/fabDashUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shop`;

const Alerts = ({ projects }) => {
  const items = projects.flatMap((p) => p.flags.filter((f) => ['late', 'over_budget', 'over_estimate', 'due_soon', 'budget_80', 'stale'].includes(f))
    .map((f) => ({ key: `${p.id}-${f}`, wo: p.work_order_number, customer: p.customer_name, f })));
  if (items.length === 0) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="fab-alerts">
      <div className="px-4 py-3 border-b border-slate-200"><h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600" /> Needs a look</h3></div>
      <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {items.map((a) => (
          <li key={a.key} className="px-4 py-2 flex items-center gap-2 text-xs">
            <span className={`shrink-0 font-semibold rounded-full px-2 py-0.5 ${FLAG_META[a.f].cls}`}>{FLAG_META[a.f].label}</span>
            <span className="font-mono text-blue-700 font-semibold">{a.wo}</span><span className="text-slate-600 truncate">{a.customer}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function FabManagerDashboard() {
  const [data, setData] = useState(null);
  const [steel, setSteel] = useState([]);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [d, sp] = await Promise.all([axios.get(`${API}/manager/dashboard`), axios.get(`${API}/steel-pricing`)]);
      setData(d.data); setSteel(sp.data); setError('');
    } catch (e) { setError(e.response?.data?.detail || 'Could not load the dashboard'); }
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); const i = setInterval(load, 60000); return () => clearInterval(i); }, [load]);

  if (!data && !error) return <div className="text-center py-10 text-slate-700" data-testid="fab-dashboard-loading">Loading the shop…</div>;
  if (error && !data) return <div className="text-center py-10 text-rose-700 bg-white rounded-xl border border-rose-200" data-testid="fab-dashboard-error">{error}</div>;

  return (
    <div className="space-y-4" data-testid="fab-manager-tab">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Fab Manager Dashboard</h2>
          <p className="text-xs text-blue-800">Live floor, every open shop job with its milestones, and running cost to date. Updated {fmtTime(data.generated_at)} · refreshes every minute.</p>
        </div>
        {data.rate_missing.length > 0 && data.cost_visible && (
          <p className="text-[11px] text-amber-800 bg-amber-100 border border-amber-200 rounded-lg px-2.5 py-1" data-testid="rate-missing-note">
            Default wage used for {data.rate_missing.join(', ')} — set pay rates for exact labour cost.
          </p>
        )}
      </div>
      <KpiStrip s={data.summary} costVisible={data.cost_visible} />
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4 items-start">
        <ProjectBoard projects={data.projects} costVisible={data.cost_visible} fabRate={data.fab_rate} />
        <div className="space-y-4">
          <FloorNow floor={data.floor} onRefresh={load} refreshing={refreshing} />
          <Alerts projects={data.projects} />
        </div>
      </div>
      <WeekPanels week={data.week} />
      <SteelPricingPanel prices={steel} onSaved={load} />
    </div>
  );
}
